// ===========================================
// Piece Library Management Page
// ===========================================
// CRUD interface for managing music pieces in the gamification system

import { useState, useRef } from 'react';
import {
  Box,
  TextField,
  Alert,
  Chip,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  FormControlLabel,
  Switch,
  Divider,
  Button,
  InputAdornment,
  IconButton,
} from '@mui/material';
import {
  MusicNote as MusicNoteIcon,
  People as PeopleIcon,
  School as SchoolIcon,
  CloudUpload as CloudUploadIcon,
  Clear as ClearIcon,
  OpenInNew as OpenInNewIcon,
  PictureAsPdf as PictureAsPdfIcon,
} from '@mui/icons-material';
import PageHeader from '../../components/common/PageHeader';
import DataTable, { Column } from '../../components/common/DataTable';
import FormModal from '../../components/common/FormModal';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import ColorPickerField from '../../components/common/ColorPickerField';
import {
  usePieces,
  useCreatePiece,
  useUpdatePiece,
  useDeletePiece,
  useUploadLessonPlan,
} from '../../hooks/useGamification';
import { Piece, CreatePieceData, UpdatePieceData } from '../../types/gamification.types';
import { useInstruments, useProgramLevels } from '../../hooks/useAdmin';
import DriveFilePicker, { SelectedDriveFile } from '../../components/googleDrive/DriveFilePicker';
import { useGoogleDriveAuthStatus } from '../../hooks/useGoogleDrive';
import PieceStagesManager from '../../components/admin/PieceStagesManager';

// ===========================================
// TYPES
// ===========================================

interface PieceFormData {
  title: string;
  composer: string;
  description: string;
  programLevelId: string;
  instrumentId: string;
  genre: string;
  gradeLevel: string;
  sheetMusicUrl: string;
  sheetMusicName: string;
  lessonPlanUrl: string;
  lessonPlanName: string;
  isActive: boolean;
  stageCount: number;
  stageNames: string[];
  stageColors: (string | null)[];
}

const DEFAULT_FORM_DATA: PieceFormData = {
  title: '',
  composer: '',
  description: '',
  programLevelId: '',
  instrumentId: '',
  genre: '',
  gradeLevel: '',
  sheetMusicUrl: '',
  sheetMusicName: '',
  lessonPlanUrl: '',
  lessonPlanName: '',
  isActive: true,
  stageCount: 3,
  stageNames: ['Stage 1', 'Stage 2', 'Stage 3'],
  stageColors: [null, null, null],
};

// ===========================================
// HELPERS
// ===========================================

function extractFileName(url: string): string {
  if (url.includes('drive.google.com')) return 'Google Drive file';
  try {
    const pathname = new URL(url).pathname;
    return pathname.split('/').pop() || 'Linked file';
  } catch {
    return 'Linked file';
  }
}

// ===========================================
// COMPONENT
// ===========================================

export default function PieceLibraryPage() {
  // Data hooks
  const [searchQuery, setSearchQuery] = useState('');
  const [activeFilter, setActiveFilter] = useState<boolean | undefined>(true);
  const { data: pieces, isLoading, error } = usePieces({
    search: searchQuery || undefined,
    isActive: activeFilter,
  });
  const { data: instruments } = useInstruments();
  const { data: programLevels } = useProgramLevels();
  const createMutation = useCreatePiece();
  const updateMutation = useUpdatePiece();
  const deleteMutation = useDeletePiece();
  const uploadLessonPlanMutation = useUploadLessonPlan();

  // Drive auth status
  const { data: driveAuthStatus } = useGoogleDriveAuthStatus();
  const driveConnected = driveAuthStatus?.isConnected ?? false;

  // Form state
  const [modalOpen, setModalOpen] = useState(false);
  const [editingPiece, setEditingPiece] = useState<Piece | null>(null);
  const [formData, setFormData] = useState<PieceFormData>(DEFAULT_FORM_DATA);

  // Delete confirmation state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [pieceToDelete, setPieceToDelete] = useState<Piece | null>(null);

  // Drive file picker state
  const [drivePickerOpen, setDrivePickerOpen] = useState(false);
  const [lessonPlanPickerOpen, setLessonPlanPickerOpen] = useState(false);

  // Hidden file input ref for lesson plan upload
  const lessonPlanFileInputRef = useRef<HTMLInputElement>(null);

  // ===========================================
  // TABLE COLUMNS
  // ===========================================

  const columns: Column<Piece>[] = [
    {
      id: 'title',
      label: 'Title',
      minWidth: 200,
      format: (value) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <MusicNoteIcon fontSize="small" color="primary" />
          <Typography variant="body2" fontWeight="medium">
            {value as string}
          </Typography>
        </Box>
      ),
    },
    {
      id: 'composer',
      label: 'Composer',
      minWidth: 150,
      format: (value) => (value ? String(value) : <Typography color="text.secondary">—</Typography>),
    },
    {
      id: 'programLevel',
      label: 'Program Level',
      minWidth: 140,
      format: (_, row) =>
        row.programLevel ? (
          <Chip
            label={row.programLevel.name}
            size="small"
            sx={{
              bgcolor: row.programLevel.color || '#4580E4',
              color: '#fff',
              fontWeight: 600,
            }}
          />
        ) : (
          <Typography color="text.secondary">—</Typography>
        ),
    },
    {
      id: 'instrument',
      label: 'Instrument',
      minWidth: 120,
      format: (_, row) =>
        row.instrument ? (
          <Chip label={row.instrument.name} size="small" variant="outlined" />
        ) : (
          <Typography color="text.secondary">—</Typography>
        ),
    },
    {
      id: '_count.skills',
      label: 'Stages',
      minWidth: 100,
      format: (_, row) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <SchoolIcon fontSize="small" color="action" />
          <Typography variant="body2">{row._count?.skills || 0}</Typography>
          {row.skills?.some((s) => s.color) && (
            <Box sx={{ display: 'flex', gap: 0.25, ml: 0.5 }}>
              {row.skills
                .filter((s) => s.color)
                .map((s) => (
                  <Box
                    key={s.id}
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      bgcolor: s.color,
                    }}
                  />
                ))}
            </Box>
          )}
        </Box>
      ),
    },
    {
      id: '_count.studentPieces',
      label: 'Students Assigned',
      minWidth: 140,
      format: (_, row) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <PeopleIcon fontSize="small" color="action" />
          <Typography variant="body2">{row._count?.studentPieces || 0}</Typography>
        </Box>
      ),
    },
    {
      id: 'isActive',
      label: 'Status',
      minWidth: 100,
      format: (value) =>
        value ? (
          <Chip label="Active" size="small" color="success" />
        ) : (
          <Chip label="Inactive" size="small" color="default" />
        ),
    },
  ];

  // ===========================================
  // HANDLERS
  // ===========================================

  const handleAdd = () => {
    setEditingPiece(null);
    setFormData(DEFAULT_FORM_DATA);
    setModalOpen(true);
  };

  const handleEdit = (piece: Piece) => {
    setEditingPiece(piece);
    setFormData({
      title: piece.title,
      composer: piece.composer || '',
      description: piece.description || '',
      programLevelId: piece.programLevelId || '',
      instrumentId: piece.instrumentId || '',
      genre: piece.genre || '',
      gradeLevel: piece.gradeLevel || '',
      sheetMusicUrl: piece.sheetMusicUrl || '',
      sheetMusicName: piece.sheetMusicUrl ? extractFileName(piece.sheetMusicUrl) : '',
      lessonPlanUrl: piece.lessonPlanUrl || '',
      lessonPlanName: piece.lessonPlanName || (piece.lessonPlanUrl ? extractFileName(piece.lessonPlanUrl) : ''),
      isActive: piece.isActive,
      stageCount: DEFAULT_FORM_DATA.stageCount,
      stageNames: [...DEFAULT_FORM_DATA.stageNames],
      stageColors: [...DEFAULT_FORM_DATA.stageColors],
    });
    setModalOpen(true);
  };

  const handleDelete = (piece: Piece) => {
    setPieceToDelete(piece);
    setDeleteDialogOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setEditingPiece(null);
  };

  const handleSubmit = async () => {
    try {
      if (editingPiece) {
        const updateData: UpdatePieceData = {
          title: formData.title,
          composer: formData.composer || null,
          description: formData.description || null,
          programLevelId: formData.programLevelId || null,
          instrumentId: formData.instrumentId || null,
          genre: formData.genre || null,
          gradeLevel: formData.gradeLevel || null,
          sheetMusicUrl: formData.sheetMusicUrl || null,
          lessonPlanUrl: formData.lessonPlanUrl || null,
          lessonPlanName: formData.lessonPlanName || null,
          lessonPlanProvider: formData.lessonPlanUrl ? 'GOOGLE_DRIVE' : null,
          isActive: formData.isActive,
        };
        await updateMutation.mutateAsync({ pieceId: editingPiece.id, data: updateData });
      } else {
        const createData: CreatePieceData = {
          title: formData.title,
          composer: formData.composer || undefined,
          description: formData.description || undefined,
          programLevelId: formData.programLevelId || undefined,
          instrumentId: formData.instrumentId || undefined,
          genre: formData.genre || undefined,
          gradeLevel: formData.gradeLevel || undefined,
          sheetMusicUrl: formData.sheetMusicUrl || undefined,
          lessonPlanUrl: formData.lessonPlanUrl || undefined,
          lessonPlanName: formData.lessonPlanName || undefined,
          lessonPlanProvider: formData.lessonPlanUrl ? 'GOOGLE_DRIVE' : undefined,
          skills: formData.stageNames
            .slice(0, formData.stageCount)
            .map((name, i) => ({
              name: name.trim() || `Stage ${i + 1}`,
              color: formData.stageColors[i] || null,
            })),
        };
        await createMutation.mutateAsync(createData);
      }
      handleCloseModal();
    } catch {
      // Error is handled by mutation
    }
  };

  const handleLessonPlanFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !editingPiece) return;
    // Reset input so the same file can be re-selected if needed
    e.target.value = '';
    try {
      const updated = await uploadLessonPlanMutation.mutateAsync({ pieceId: editingPiece.id, file });
      // No skillId supplied → backend returns the updated Piece
      const url = (updated as { lessonPlanUrl?: string | null }).lessonPlanUrl ?? '';
      const name = (updated as { lessonPlanName?: string | null }).lessonPlanName ?? '';
      setFormData((prev) => ({
        ...prev,
        lessonPlanUrl: url,
        lessonPlanName: name || (url ? extractFileName(url) : ''),
      }));
    } catch {
      // Error handled by mutation
    }
  };

  const handleConfirmDelete = async () => {
    if (pieceToDelete) {
      try {
        await deleteMutation.mutateAsync(pieceToDelete.id);
        setDeleteDialogOpen(false);
        setPieceToDelete(null);
      } catch {
        // Error is handled by mutation
      }
    }
  };

  const isFormLoading = createMutation.isPending || updateMutation.isPending;
  const isDeleteLoading = deleteMutation.isPending;

  // ===========================================
  // RENDER
  // ===========================================

  return (
    <Box>
      <PageHeader
        title="Piece Library"
        subtitle="Manage music pieces and skills for student learning"
        breadcrumbs={[
          { label: 'Admin', path: '/admin' },
          { label: 'Piece Library' },
        ]}
        actionLabel="Add Piece"
        onAction={handleAdd}
      />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to load pieces. Please try again.
        </Alert>
      )}

      {/* Filters */}
      <Box sx={{ mb: 2, display: 'flex', gap: 2, alignItems: 'center' }}>
        <TextField
          label="Search pieces"
          placeholder="Search by title, composer..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          size="small"
          sx={{ minWidth: 300 }}
        />
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Status</InputLabel>
          <Select
            value={activeFilter === undefined ? 'all' : activeFilter ? 'active' : 'inactive'}
            onChange={(e) => {
              const value = e.target.value;
              setActiveFilter(value === 'all' ? undefined : value === 'active');
            }}
            label="Status"
          >
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="active">Active</MenuItem>
            <MenuItem value="inactive">Inactive</MenuItem>
          </Select>
        </FormControl>
      </Box>

      <DataTable
        columns={columns}
        data={pieces?.data ?? []}
        loading={isLoading}
        onEdit={handleEdit}
        onDelete={handleDelete}
        searchable={false}
        emptyMessage="No pieces found. Click 'Add Piece' to create one."
      />

      {/* Add/Edit Modal */}
      <FormModal
        open={modalOpen}
        title={editingPiece ? 'Edit Piece' : 'Add Piece'}
        submitLabel={editingPiece ? 'Update' : 'Create'}
        loading={isFormLoading}
        onSubmit={handleSubmit}
        onClose={handleCloseModal}
        maxWidth="sm"
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          {/* Title */}
          <TextField
            label="Title"
            value={formData.title}
            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
            required
            fullWidth
            placeholder="e.g., Für Elise"
          />

          {/* Composer */}
          <TextField
            label="Composer"
            value={formData.composer}
            onChange={(e) => setFormData({ ...formData, composer: e.target.value })}
            fullWidth
            placeholder="e.g., Ludwig van Beethoven"
          />

          {/* Description */}
          <TextField
            label="Description"
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            fullWidth
            multiline
            rows={3}
            placeholder="Brief description of the piece..."
          />

          {/* Program Level and Instrument */}
          <Stack direction="row" spacing={2}>
            <FormControl fullWidth>
              <InputLabel>Program Level</InputLabel>
              <Select
                value={formData.programLevelId}
                onChange={(e) => setFormData({ ...formData, programLevelId: e.target.value })}
                label="Program Level"
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {programLevels
                  ?.filter((pl) => pl.isActive)
                  .map((pl) => (
                    <MenuItem key={pl.id} value={pl.id}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {pl.color && (
                          <Box
                            sx={{
                              width: 12,
                              height: 12,
                              borderRadius: '50%',
                              bgcolor: pl.color,
                              flexShrink: 0,
                            }}
                          />
                        )}
                        {pl.name}
                      </Box>
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>Instrument</InputLabel>
              <Select
                value={formData.instrumentId}
                onChange={(e) => setFormData({ ...formData, instrumentId: e.target.value })}
                label="Instrument"
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {instruments?.map((instrument) => (
                  <MenuItem key={instrument.id} value={instrument.id}>
                    {instrument.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>

          {/* Genre and Grade Level */}
          <Stack direction="row" spacing={2}>
            <TextField
              label="Genre"
              value={formData.genre}
              onChange={(e) => setFormData({ ...formData, genre: e.target.value })}
              fullWidth
              placeholder="e.g., Classical, Jazz, Pop"
            />
            <TextField
              label="Grade Level"
              value={formData.gradeLevel}
              onChange={(e) => setFormData({ ...formData, gradeLevel: e.target.value })}
              fullWidth
              placeholder="e.g., Grade 1, Beginner"
            />
          </Stack>

          {/* Music Score from Google Drive */}
          <TextField
            label="Music Score"
            value={formData.sheetMusicName || formData.sheetMusicUrl}
            placeholder="Select from Google Drive..."
            fullWidth
            InputProps={{
              readOnly: true,
              startAdornment: formData.sheetMusicUrl ? (
                <InputAdornment position="start">
                  <MusicNoteIcon fontSize="small" color="primary" />
                </InputAdornment>
              ) : undefined,
              endAdornment: (
                <InputAdornment position="end">
                  {formData.sheetMusicUrl && (
                    <>
                      <IconButton
                        size="small"
                        onClick={() => window.open(formData.sheetMusicUrl, '_blank', 'noopener,noreferrer')}
                        title="Open in Google Drive"
                      >
                        <OpenInNewIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => setFormData({ ...formData, sheetMusicUrl: '', sheetMusicName: '' })}
                        title="Remove"
                      >
                        <ClearIcon fontSize="small" />
                      </IconButton>
                    </>
                  )}
                  <Button
                    size="small"
                    startIcon={<CloudUploadIcon />}
                    onClick={() => setDrivePickerOpen(true)}
                    sx={{ ml: 0.5 }}
                  >
                    Browse
                  </Button>
                </InputAdornment>
              ),
            }}
          />

          {/* Lesson Plan */}
          <TextField
            label="Lesson Plan"
            value={formData.lessonPlanName || formData.lessonPlanUrl}
            placeholder={driveConnected ? 'Select PDF from Google Drive or upload...' : 'Connect Google Drive to attach lesson plans.'}
            fullWidth
            InputProps={{
              readOnly: true,
              startAdornment: formData.lessonPlanUrl ? (
                <InputAdornment position="start">
                  <PictureAsPdfIcon fontSize="small" color="error" />
                </InputAdornment>
              ) : undefined,
              endAdornment: (
                <InputAdornment position="end">
                  {formData.lessonPlanUrl && (
                    <>
                      <IconButton
                        size="small"
                        onClick={() => window.open(formData.lessonPlanUrl, '_blank', 'noopener,noreferrer')}
                        title="Open lesson plan"
                      >
                        <OpenInNewIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => setFormData({ ...formData, lessonPlanUrl: '', lessonPlanName: '' })}
                        title="Remove"
                      >
                        <ClearIcon fontSize="small" />
                      </IconButton>
                    </>
                  )}
                  <Button
                    size="small"
                    startIcon={<CloudUploadIcon />}
                    onClick={() => setLessonPlanPickerOpen(true)}
                    disabled={!driveConnected}
                    sx={{ ml: 0.5 }}
                  >
                    Browse
                  </Button>
                  {editingPiece && (
                    <Button
                      size="small"
                      onClick={() => lessonPlanFileInputRef.current?.click()}
                      disabled={!driveConnected || uploadLessonPlanMutation.isPending}
                      sx={{ ml: 0.5 }}
                    >
                      Upload
                    </Button>
                  )}
                </InputAdornment>
              ),
            }}
          />
          {!editingPiece && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: -1 }}>
              Save the piece first to upload a file.
            </Typography>
          )}
          {!driveConnected && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: -1 }}>
              Connect Google Drive to attach lesson plans.
            </Typography>
          )}

          {/* Hidden file input for lesson plan upload */}
          <input
            ref={lessonPlanFileInputRef}
            type="file"
            accept="application/pdf"
            style={{ display: 'none' }}
            onChange={handleLessonPlanFileChange}
          />

          {/* Learning Stages - Show manager when editing, bulk creator when new */}
          {editingPiece ? (
            <>
              <Divider />
              <Typography variant="subtitle2" color="text.secondary">
                Learning Stages
              </Typography>
              <PieceStagesManager
                pieceId={editingPiece.id}
                driveConnected={driveConnected}
              />
            </>
          ) : null}

          {/* Learning Stages - Only show when creating */}
          {!editingPiece && (
            <>
              <Divider />
              <Typography variant="subtitle2" color="text.secondary">
                Learning Stages
              </Typography>

              <TextField
                label="Number of Stages"
                type="number"
                value={formData.stageCount}
                onChange={(e) => {
                  const count = Math.max(1, Math.min(20, parseInt(e.target.value) || 1));
                  const newNames = [...formData.stageNames];
                  const newColors = [...formData.stageColors];
                  // Generate defaults for new indices
                  for (let i = newNames.length; i < count; i++) {
                    newNames.push(`Stage ${i + 1}`);
                  }
                  for (let i = newColors.length; i < count; i++) {
                    newColors.push(null);
                  }
                  setFormData({
                    ...formData,
                    stageCount: count,
                    stageNames: newNames.slice(0, count),
                    stageColors: newColors.slice(0, count),
                  });
                }}
                inputProps={{ min: 1, max: 20 }}
                fullWidth
              />

              {formData.stageNames.slice(0, formData.stageCount).map((name, index) => (
                <Stack key={index} direction="row" spacing={2} alignItems="flex-start">
                  <TextField
                    label={`Stage ${index + 1}`}
                    value={name}
                    onChange={(e) => {
                      const newNames = [...formData.stageNames];
                      newNames[index] = e.target.value;
                      setFormData({ ...formData, stageNames: newNames });
                    }}
                    fullWidth
                    size="small"
                  />
                  <ColorPickerField
                    label=""
                    value={formData.stageColors[index] ?? null}
                    onChange={(color) => {
                      const newColors = [...formData.stageColors];
                      newColors[index] = color;
                      setFormData({ ...formData, stageColors: newColors });
                    }}
                    showClear
                    useSchoolPalette
                  />
                </Stack>
              ))}
            </>
          )}

          {/* Active Status - Only show when editing */}
          {editingPiece && (
            <FormControlLabel
              control={
                <Switch
                  checked={formData.isActive}
                  onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                />
              }
              label={formData.isActive ? 'Active' : 'Inactive'}
            />
          )}
        </Box>
      </FormModal>

      {/* Delete Confirmation */}
      <ConfirmDialog
        open={deleteDialogOpen}
        title="Delete Piece"
        message={`Are you sure you want to delete "${pieceToDelete?.title}"? This action cannot be undone.`}
        confirmLabel="Delete"
        confirmColor="error"
        loading={isDeleteLoading}
        onConfirm={handleConfirmDelete}
        onCancel={() => setDeleteDialogOpen(false)}
      />

      {/* Google Drive File Picker - Music Score */}
      <DriveFilePicker
        open={drivePickerOpen}
        onClose={() => setDrivePickerOpen(false)}
        onSelect={(file: SelectedDriveFile) => {
          setFormData({
            ...formData,
            sheetMusicUrl: file.webViewLink,
            sheetMusicName: file.name,
          });
        }}
        title="Select Music Score"
      />

      {/* Google Drive File Picker - Lesson Plan */}
      <DriveFilePicker
        open={lessonPlanPickerOpen}
        onClose={() => setLessonPlanPickerOpen(false)}
        onSelect={(file: SelectedDriveFile) => {
          setFormData({
            ...formData,
            lessonPlanUrl: file.webViewLink,
            lessonPlanName: file.name,
          });
        }}
        title="Select Lesson Plan"
        fileTypes={['pdf']}
      />
    </Box>
  );
}
